import React, { useState, useEffect, createContext, useContext } from "react";
import { User, Session } from "@supabase/supabase-js";
import { createClient } from "@/lib/supabase/client";
import { identifyUser } from "@/utils/posthog";

// Define the Profile type
export interface Profile {
    id: string;
    email: string;
    full_name: string;
    avatar_url: string;
    credits: number;
    // Add other profile fields as needed
}

// Define the Auth context type
interface AuthContextType {
    user: User | null;
    profile: Profile | null;
    session: Session | null;
    isVerified: boolean;
    verificationChecked: boolean;
    isLoading: boolean;
    isProfileLoading: boolean;
    refreshProfile: () => Promise<void>;
}

// Create the context with default values
export const AuthContext = createContext<AuthContextType>({
    user: null,
    profile: null,
    session: null,
    isVerified: false,
    verificationChecked: false,
    isLoading: true,
    isProfileLoading: false,
    refreshProfile: async () => { },
});

export const useAuthContext = () => useContext(AuthContext);

interface AuthContextProviderProps {
    children: React.ReactNode;
}

export const AuthContextProvider = ({ children }: AuthContextProviderProps) => {
    const [user, setUser] = useState<User | null>(null);
    const [profile, setProfile] = useState<Profile | null>(null);
    const [session, setSession] = useState<Session | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isProfileLoading, setIsProfileLoading] = useState<boolean>(false);
    const [isVerified, setIsVerified] = useState<boolean>(false);
    const [verificationChecked, setVerificationChecked] = useState<boolean>(false);

    const supabase = createClient();

    // Function to check user verification status
    const checkUserVerification = (user: User | null) => {
        if (!user) {
            setIsVerified(false);
            setVerificationChecked(true);
            return;
        }

        // Check if email is confirmed
        if (user.email_confirmed_at) {
            setIsVerified(true);
        } else {
            setIsVerified(false);
        }
        setVerificationChecked(true);
    };

    const fetchProfile = async (userId: string) => {
        try {
            setIsProfileLoading(true);
            const { data, error } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', userId)
                .single();

            if (error) {
                console.error('Error fetching profile:', error);
                return;
            }

            if (data) {
                setProfile(data);
            }
        } catch (error) {
            console.error('Error in fetchProfile:', error);
        } finally {
            setIsProfileLoading(false);
        }
    };

    const refreshProfile = async () => {
        if (user?.id) {
            await fetchProfile(user.id);
        }
    };

    useEffect(() => {
        const initializeAuth = async () => {
            try {
                // CRITICAL: Get the existing session from storage
                const { data: { session: existingSession } } = await supabase.auth.getSession();

                if (existingSession) {
                    setSession(existingSession);
                    setUser(existingSession.user);
                    checkUserVerification(existingSession.user);
                    // Fetch profile immediately if we have a user
                    if (existingSession.user) {
                        fetchProfile(existingSession.user.id);
                    }
                }

                setIsLoading(false);
            } catch (error) {
                // console.error('Error initializing auth:', error);
                setIsLoading(false);
            }
        };

        initializeAuth();

        // Listen for auth changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
            setSession(newSession);
            setUser(newSession?.user ?? null);
            checkUserVerification(newSession?.user ?? null);

            if (newSession?.user) {
                // If user changed or just signed in, fetch profile
                if (newSession.user.id !== user?.id) {
                    fetchProfile(newSession.user.id);
                }

                identifyUser(newSession.user.id, {
                    email: newSession.user.email,
                    name: newSession.user.user_metadata?.full_name || newSession.user.user_metadata?.name,
                    signUpDate: newSession.user.created_at
                });
            } else {
                setProfile(null);
            }

            setIsLoading(false);
        });

        return () => {
            subscription.unsubscribe();
        };
    }, []);

    return (
        <AuthContext.Provider value={{ user, profile, session, isVerified, verificationChecked, isLoading, isProfileLoading, refreshProfile }}>
            {children}
        </AuthContext.Provider>
    );
};
